/**
 * Project page header: "Checkouts: Mac ✓ · Photon —" plus Add machine (1 click, 1 passkey) and a
 * per-checkout menu (Forget on <machine>, which never deletes files; This machine runs landings).
 * Machines' own encrypted reports are the source of truth for what is checked out where.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Laptop, MoreHorizontal, Plus } from 'lucide-react'
import { useState } from 'react'
import {
  type CheckoutProgress,
  checkoutOk,
  checkoutsOf,
  requestCheckouts,
  slugify,
} from '../lib/account-projects.js'
import { useBackend } from '../lib/backend.js'
import { qk, useMachines } from '../lib/queries.js'
import type { Machine, Project } from '../lib/types.js'
import '../routes/project-views.css'
import { CheckoutRows, useCheckoutDeps } from './NewProjectDialog.js'
import { Button, errorText, Sheet, useToast } from './ui.js'

/** The remote a new checkout should clone: the entity's, else what a checkout reported. */
export function projectRemote(project: Project, machines: readonly Machine[]): string | undefined {
  if (project.remote) return project.remote
  const seen = checkoutsOf(project.id, machines).find((c) => c.checkout.remote)?.checkout.remote
  if (seen) return /^[a-z0-9.-]+\.[a-z]{2,}\//i.test(seen) ? `https://${seen}` : seen
  return project.config?.repos?.[0]?.remote ?? undefined
}

export function ProjectCheckouts({ project }: { project: Project }) {
  const machines = useMachines()
  const list = machines.data ?? []
  const have = checkoutsOf(project.id, list)
  const [adding, setAdding] = useState(false)
  const [menu, setMenu] = useState<string>()
  return (
    <div className="pv-checkouts">
      {list.length === 0 ? (
        <p className="pv-empty">No machines yet. Add one to check this project out on it.</p>
      ) : (
        <ul className="pv-rows" aria-label="Checkouts">
          {list.map((m) => {
            const c = have.find((h) => h.machine.id === m.id)
            return (
              <li key={m.id}>
                <div className="pv-row">
                  <span className="pv-row__icon">
                    <Laptop
                      size={16}
                      role="img"
                      aria-label={c ? 'checked out' : 'not checked out'}
                      className={c ? 'pv-word-ok' : 'text-text-3'}
                    />
                  </span>
                  <span className="pv-row__main">
                    <span className="pv-row__title">
                      {m.name}
                      {m.online ? null : <span className="pv-sub"> · offline</span>}
                    </span>
                    <span className="pv-row__meta">
                      {c ? (
                        <>
                          {c.checkout.path ? (
                            <code className="pv-mono">{c.checkout.path}</code>
                          ) : (
                            'checked out'
                          )}
                          {c.checkout.landing ? ' · runs landings' : ''}
                        </>
                      ) : (
                        'not checked out here'
                      )}
                    </span>
                  </span>
                  <span className="pv-row__aside pv-rel">
                    {c ? (
                      <button
                        type="button"
                        className="pv-btn pv-btn--icon"
                        aria-haspopup="menu"
                        aria-expanded={menu === m.id}
                        aria-label={`${m.name} checkout options`}
                        title={c.checkout.path}
                        onClick={() => setMenu(menu === m.id ? undefined : m.id)}
                      >
                        <MoreHorizontal size={16} aria-hidden="true" />
                      </button>
                    ) : null}
                    {menu === m.id && c ? (
                      <CheckoutMenu
                        project={project}
                        machine={m}
                        landing={c.checkout.landing}
                        onClose={() => setMenu(undefined)}
                      />
                    ) : null}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      <div className="pv-actions">
        <button type="button" className="pv-btn" onClick={() => setAdding(true)}>
          <Plus size={16} aria-hidden="true" />
          Add machine
        </button>
      </div>
      {adding ? <AddMachineDialog project={project} onClose={() => setAdding(false)} /> : null}
      <MigrateConfigCard project={project} checkouts={have} />
    </div>
  )
}

/** v1 files still name machines: one click proposes the v2 file through landing (never forced). */
function MigrateConfigCard({
  project,
  checkouts,
}: {
  project: Project
  checkouts: ReturnType<typeof checkoutsOf>
}) {
  const b = useBackend()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const v1 = checkouts.filter((c) => c.checkout.configVersion === 1)
  const target =
    v1.find((c) => c.checkout.landing && c.machine.online) ?? v1.find((c) => c.machine.online)
  if (!v1.length) return null
  return (
    <div
      role="note"
      className="flex w-full flex-wrap items-center gap-2 rounded-lg bg-status-wait-soft px-3 py-2 text-[14px]"
    >
      <span className="min-w-0 flex-1">
        viko.project.yaml still names machines (v1). Migrate it to v2: machine paths and pins move
        to each machine.
      </span>
      <Button
        size="sm"
        variant="primary"
        disabled={busy || !target}
        title={target ? undefined : 'Bring a machine with this checkout online'}
        onClick={async () => {
          if (!target) return
          setBusy(true)
          try {
            const out = await b.dispatch('project.migrate_config', target.machine.id, {
              projectId: project.id,
            })
            const r = out.result as { status?: string; reason?: string; landingId?: string }
            toast(
              r?.status === 'proposed'
                ? r.landingId
                  ? 'Migration committed; it lands when the checks pass.'
                  : 'Migration committed on a Viko branch; land it from the Landing tab.'
                : (r?.reason ?? 'Nothing to migrate.'),
              r?.status === 'failed' ? 'error' : undefined,
            )
          } catch (e) {
            toast(errorText(e), 'error')
          } finally {
            setBusy(false)
          }
        }}
      >
        {busy ? 'Migrating…' : 'Migrate'}
      </Button>
    </div>
  )
}

function CheckoutMenu({
  project,
  machine,
  landing,
  onClose,
}: {
  project: Project
  machine: Machine
  landing: boolean
  onClose(): void
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const run = async (what: () => Promise<unknown>, done: string) => {
    setBusy(true)
    try {
      await what()
      toast(done)
      await qc.invalidateQueries({ queryKey: qk.machines })
      onClose()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div
      role="menu"
      aria-label={`${machine.name} checkout`}
      className="card absolute top-full left-0 z-20 mt-1 grid min-w-[16rem] gap-1 p-2"
    >
      <button
        type="button"
        role="menuitem"
        disabled={busy || !machine.online}
        className="rounded px-2 py-1.5 text-left hover:bg-surface-2"
        onClick={() =>
          void run(
            () =>
              b.dispatch('project.policy', machine.id, {
                projectId: project.id,
                local: { landing: !landing },
              }),
            landing ? `${machine.name} no longer runs landings` : `${machine.name} runs landings`,
          )
        }
      >
        {landing ? 'Stop running landings here' : 'Run landings on this machine'}
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={busy || !machine.online}
        className="rounded px-2 py-1.5 text-left text-status-fail hover:bg-surface-2"
        onClick={() =>
          void run(
            () => b.dispatch('project.forget', machine.id, { projectId: project.id }),
            `Forgot ${project.name} on ${machine.name}; its files are untouched`,
          )
        }
      >
        Forget on {machine.name} (keeps files)
      </button>
      {!machine.online ? (
        <p className="m-0 px-2 text-[14px] text-text-3">{machine.name} is offline.</p>
      ) : null}
    </div>
  )
}

export function AddMachineDialog({
  project,
  onClose,
  preselect,
}: {
  project: Project
  onClose(): void
  preselect?: string
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const deps = useCheckoutDeps()
  const machines = useMachines()
  const list = machines.data ?? []
  const have = new Set(checkoutsOf(project.id, list).map((c) => c.machine.id))
  const options = list.filter((m) => !have.has(m.id))
  const [machineId, setMachineId] = useState(preselect ?? options.find((m) => m.online)?.id ?? '')
  const [rows, setRows] = useState<CheckoutProgress[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const remote = projectRemote(project, list)
  const machine = list.find((m) => m.id === machineId)
  const problem = !remote
    ? 'This project has no repository yet. Create it from a machine that has a checkout.'
    : !machine
      ? 'Pick a machine.'
      : !machine.online
        ? `${machine.name} is offline.`
        : ''

  const add = async () => {
    if (problem || !remote || busy) return
    setBusy(true)
    setError('')
    try {
      const { statement, results } = await requestCheckouts(
        deps,
        {
          projectId: project.id,
          machineIds: [machineId],
          repo: { kind: 'existing', remote },
          dirName: slugify(project.config?.project?.id ?? project.name),
        },
        (p) => setRows([p]),
      )
      await b.api.recordCheckouts(project.id, {
        machineIds: [machineId],
        statementId: statement.statementId,
        via: 'checkout',
      })
      await qc.invalidateQueries({ queryKey: qk.machines })
      await qc.invalidateQueries({ queryKey: qk.projects })
      if (results.every(checkoutOk)) onClose()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Check out ${project.name}`}
      subtitle="One passkey approves cloning it on the machine you pick."
      placement="center"
      size="md"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!!problem || busy} onClick={() => void add()}>
            {busy ? 'Checking out…' : 'Check out'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-2">
        {options.length === 0 ? (
          <p className="m-0 text-[14px] text-text-3">Every machine already has a checkout.</p>
        ) : (
          <div className="choice" role="radiogroup" aria-label="Machine">
            {options.map((m) => (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={m.id === machineId}
                disabled={!m.online || busy}
                title={m.online ? undefined : `${m.name} is offline`}
                onClick={() => setMachineId(m.id)}
              >
                <Laptop size={14} aria-hidden="true" />
                {m.name}
              </button>
            ))}
          </div>
        )}
        {remote ? (
          <p className="m-0 break-all text-[14px] text-text-3">Repository: {remote}</p>
        ) : null}
        <CheckoutRows rows={rows} machines={list} creating={false} />
        {error || (problem && options.length) ? (
          <p role={error ? 'alert' : undefined} className="m-0 text-[14px] text-status-fail">
            {error || problem}
          </p>
        ) : null}
      </div>
    </Sheet>
  )
}
